import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./BillsFilter.css";

function BillsFilter({ currentUser }) {
  const [bills, setBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [products, setProducts] = useState([]);
  const [productNames, setProductNames] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    product: "",
    payment: "",
    location: currentUser?.localisation?._id || "", // filtre localisation initial
  });
  const [loading, setLoading] = useState(false);

  // 🔹 Détails produit / sous-produit
  const getProductDetails = useCallback(
    (item) => {
      const mainProduct = products.find((p) => p._id === item.id);
      if (mainProduct) {
        return {
          parent: mainProduct.name,
          name: null,
          price: item.price || mainProduct.price,
          localisation: mainProduct.localisation || null,
        };
      }

      for (const product of products) {
        const subProduct = product.subProductsV?.find((sp) => sp._id === item.id);
        if (subProduct) {
          return {
            parent: product.name,
            name: subProduct.name,
            price: item.price || subProduct.price,
            localisation: subProduct.localisation || null,
          };
        }
      }

      return { name: item.name || "Produit inconnu", parent: null, price: item.price || 0, localisation: null };
    },
    [products]
  );

  // 🔹 Charger produits, factures et localisations
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Charger tout en parallèle
      const [productsRes, billsRes, locRes] = await Promise.all([
        axios.get("/api/ProductV/getProductV", {
          headers: { Authorised: token },
          params: { localisation: currentUser?.localisation?._id || "" },
        }),
        axios.get("/api/BillsValises/getAllBillsV", {
          headers: { Authorised: token },
        }),
        axios.get("/api/Locations/getAllLocations"),
      ]);

      const productList = Array.isArray(productsRes.data) ? productsRes.data : [];
      setProducts(productList);

      const billsList = Array.isArray(billsRes.data) ? billsRes.data : [];
      setBills(billsList);
      setFilteredBills(billsList);

      const locList = Array.isArray(locRes.data) ? locRes.data : [];
      setLocations(locList);

      // Liste noms produits pour filtre
      const namesSet = new Set();
      productList.forEach((p) => {
        if (p.name) namesSet.add(p.name);
        p.subProductsV?.forEach((sp) => {
          if (sp.name) namesSet.add(`${p.name} → ${sp.name}`);
        });
      });
      setProductNames([...namesSet].sort());
    } catch (err) {
      console.error("Erreur lors du chargement :", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // 🔹 Appliquer filtres
  useEffect(() => {
    let data = [...bills];

    data = data.filter((bill) => {
      // Filtre date
      if (filters.startDate && new Date(bill.date) < new Date(filters.startDate)) return false;
      if (filters.endDate && new Date(bill.date) > new Date(filters.endDate)) return false;

      // Filtre produit
      if (filters.product) {
        const matchesProduct = bill.items.some((item) => {
          const details = getProductDetails(item);
          const fullName = details.parent ? `${details.parent} → ${details.name || ""}`.trim() : details.name;
          return fullName === filters.product;
        });
        if (!matchesProduct) return false;
      }

      // Filtre moyen de paiement
      if (filters.payment && bill.paymentMethod !== filters.payment) return false;

      // Filtre localisation
      if (filters.location) {
        const matchesLocation = bill.items.some((item) => {
          const details = getProductDetails(item);
          return details.localisation?.toString() === filters.location.toString();
        });
        if (!matchesLocation) return false;
      }

      return true;
    });

    setFilteredBills(data);
  }, [bills, filters, getProductDetails]);

  // 🔹 Calcul total
  const totalAmount = filteredBills.reduce((sum, bill) => {
    bill.items.forEach((item) => {
      const details = getProductDetails(item);
      const qty = item.quantity || 1;
      sum += (details.price || 0) * qty;
    });
    return sum;
  }, 0);

  return (
    <div className="bills-filter">
      <h2>📊 Rapport des factures</h2>

      {/* Filtres */}
      <div className="filters">
        <input type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} />
        <input type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} />
        <select value={filters.product} onChange={(e) => setFilters({ ...filters, product: e.target.value })}>
          <option value="">Tous les produits</option>
          {productNames.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
        <select value={filters.payment} onChange={(e) => setFilters({ ...filters, payment: e.target.value })}>
          <option value="">Tous moyens de paiement</option>
          <option value="Espece">Espèce</option>
          <option value="TPE">TPE</option>
        </select>
        <select value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })}>
          <option value="">Toutes les localisations</option>
          {locations.map((loc) => <option key={loc._id} value={loc._id}>{loc.name}</option>)}
        </select>
      </div>

      {/* Tableau */}
      {loading ? <p>Chargement...</p> : (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Produit</th>
              <th>Sous-produit</th>
              <th>Quantité</th>
              <th>Prix</th>
              <th>Moyen de paiement</th>
            </tr>
          </thead>
          <tbody>
            {filteredBills.map((bill) =>
              bill.items.map((item, idx) => {
                const details = getProductDetails(item);
                return (
                  <tr key={bill._id + idx}>
                    <td>{new Date(bill.date).toLocaleDateString()}</td>
                    <td>{details.parent || details.name}</td>
                    <td>{details.parent ? details.name : "-"}</td>
                    <td>{item.quantity || 1}</td>
                    <td>{details.price} DT</td>
                    <td>{bill.paymentMethod || "-"}</td>
                  </tr>
                );
              })
            )}
            <tr className="total-row">
              <td colSpan="4" style={{ fontWeight: "bold", textAlign: "right" }}>Total :</td>
              <td style={{ fontWeight: "bold" }}>{totalAmount.toFixed(2)} DT</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

export default BillsFilter;
